import React from 'react';
import { Link } from 'react-router-dom';
import {
    CALL_CONTENT_MAX_LENGTH,
    CALL_LANGUAGE_OPTIONS,
    CALL_VOICE_OPTIONS,
} from './callTemplateOptions';

/**
 * Shared chrome + field layout for Create / Edit ECX Call Template pages.
 */
export function AdminCallTemplatePage({
    title,
    subtitle,
    headerBadge,
    backTo = '/admin-templates',
    backLabel = 'Back to templates',
    children,
    footer,
}) {
    return (
        <div className="container container-xxl admin-call-template-page">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h1 className="mb-1">
                        <i className="bi bi-telephone text-info me-2"></i>
                        {title}
                    </h1>
                    {subtitle ? (
                        <p className="text-muted small mb-0">{subtitle}</p>
                    ) : null}
                </div>
                <Link
                    to={backTo}
                    className="btn btn-outline-secondary btn-sm"
                >
                    <i className="bi bi-arrow-left me-1"></i>
                    {backLabel}
                </Link>
            </div>

            <div className="card shadow-sm border-0 admin-call-template-card">
                <div className="card-header bg-white border-bottom admin-call-template-card__header">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="admin-call-template-card__icon">
                                <i className="bi bi-telephone-outbound"></i>
                            </span>
                            <div>
                                <h5 className="card-title mb-0">{title}</h5>
                                <span className="badge admin-templates-medium-badge admin-templates-medium-badge--call">
                                    CALL
                                </span>
                            </div>
                        </div>
                        {headerBadge}
                    </div>
                </div>
                <div className="card-body p-3 p-md-4">
                    {children}
                </div>
                {footer ? (
                    <div className="card-footer bg-white border-top admin-call-template-card__footer">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export function AdminCallTemplateMetaGrid({ items }) {
    if (!items?.length) return null;
    return (
        <div className="admin-call-template-meta mb-4">
            <div className="row g-3">
                {items.map((item) => (
                    <div key={item.label} className="col-6 col-md-3">
                        <div className="admin-call-template-meta__item">
                            <div className="admin-call-template-meta__label">{item.label}</div>
                            <div
                                className={`admin-call-template-meta__value ${item.mono ? 'font-monospace small' : ''}`}
                                title={item.title || (typeof item.value === 'string' ? item.value : undefined)}
                            >
                                {item.value ?? '—'}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminCallTemplateFields({
    formData,
    onChange,
    reminderNameControl,
    usedByDisabled = false,
    hideUsedBy = false,
    contentField = 'content',
}) {
    const contentValue = formData[contentField] || '';

    return (
        <>
            <section className="admin-call-template-section mb-4">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-info-circle me-2"></i>
                    Template
                </h6>
                <div className="row g-3">
                    <div className={hideUsedBy ? 'col-12' : 'col-md-6'}>
                        <label className="form-label fw-semibold">Template name *</label>
                        {reminderNameControl}
                    </div>
                    {!hideUsedBy && (
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Used by *</label>
                            <select
                                className="form-select"
                                name="usedBy"
                                value={formData.usedBy}
                                onChange={onChange}
                                required
                                disabled={usedByDisabled}
                            >
                                <option value="CLINIC">Clinic</option>
                                <option value="ECX">ECX</option>
                            </select>
                            <div className="form-text">
                                {usedByDisabled
                                    ? 'Set automatically from the reminder type'
                                    : 'Who this master template is intended for'}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="admin-call-template-section mb-4">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-soundwave me-2"></i>
                    Voice settings
                </h6>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Twilio voice *</label>
                        <select
                            className="form-select"
                            name="voice"
                            value={formData.voice}
                            onChange={onChange}
                            required
                        >
                            {CALL_VOICE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Used for Twilio &lt;Say&gt; on live calls
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Language *</label>
                        <select
                            className="form-select"
                            name="language"
                            value={formData.language}
                            onChange={onChange}
                            required
                        >
                            {CALL_LANGUAGE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Locale / accent for &lt;Say&gt; (e.g. en-US, en-GB)
                        </div>
                    </div>
                </div>
            </section>

            <section className="admin-call-template-section mb-0">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-chat-left-text me-2"></i>
                    Call script
                </h6>
                <label className="form-label fw-semibold">Script content *</label>
                <textarea
                    className="form-control admin-call-template-script"
                    rows={10}
                    name={contentField}
                    value={contentValue}
                    onChange={onChange}
                    placeholder="Enter the plain-text script Twilio will speak"
                    maxLength={CALL_CONTENT_MAX_LENGTH}
                    required
                />
                <div className="d-flex flex-wrap justify-content-between gap-2 mt-1">
                    <div className="form-text mb-0">
                        Variables:{" "}
                        <code>{"{PATIENT_NAME}"}</code>,{" "}
                        <code>{"{CLINIC_NAME}"}</code>,{" "}
                        <code>{"{APPOINTMENT_DATE}"}</code>,{" "}
                        <code>{"{APPOINTMENT_TIME}"}</code>
                    </div>
                    <div className="form-text mb-0">
                        {contentValue.length}/{CALL_CONTENT_MAX_LENGTH}
                    </div>
                </div>
            </section>
        </>
    );
}

export function AdminCallTemplateFooter({
    left,
    onCancel,
    onSubmitLabel,
    submitting,
    submitDisabled,
    submitVariant = 'info',
}) {
    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>{left}</div>
            <div className="d-flex flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancel
                </button>
                <button
                    type="submit"
                    className={`btn btn-${submitVariant} btn-sm text-white`}
                    disabled={submitDisabled || submitting}
                >
                    {submitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-lg me-1"></i>
                            {onSubmitLabel}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
